import * as jose from 'jose';
import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { isValidDate } from 'utils';
import { WGS_ENTITLEMENT } from 'utils/constants';
import BRAND_CODES from 'brand-config/brand-codes';

const selectSessionState = (state: RootState) => state.session;

// Read brand code directly off state to avoid an import cycle
// (brand-selectors → …). Kept internal to sessionSelectors — every
// external consumer still goes through `services/brand/brandSelectors`.
const selectSessionBrandCode = (state: RootState) => state.brand?.code ?? '';

export const selectUserSignInStatus = createSelector(
  [selectSessionState],
  (sessionState) => sessionState.userLoggedIn,
);

export const selectEntitlements = createSelector(
  [selectSessionState],
  (sessionState) => sessionState.entitlements,
);

export const selectHasAddressMonitoring = createSelector(
  [selectEntitlements],
  (entitlements) => {
    const value = entitlements?.['itps:address_monitoring'];
    if (Array.isArray(value)) return value.length > 0;
    return !!value;
  },
);

// JWT `itps:can_invite` entitlement — true when the user is a primary
// family-plan holder who can add or invite members. Drives the
// FamilyCard dashboard tile visibility and gates the Add Member button
// (Angular parity — `@Feature(ClientFeature.feat_can_invite)`).
export const selectCanInvite = createSelector(
  [selectEntitlements],
  (entitlements) => {
    const value = entitlements?.['itps:can_invite'];
    if (Array.isArray(value)) return value.length > 0;
    return !!value;
  },
);

// JWT `itps:breach` entitlement — granted to MinorDefense family
// plan holders. When true, the "Add member" modal opens directly on
// the use-code redemption screen (Angular parity —
// `family-add-member.component.ts:66` sets `minorBreach = true` from
// this same entitlement and routes ADD_CHILD through ADD_USECODE).
export const selectHasBreach = createSelector(
  [selectEntitlements],
  (entitlements) => {
    const value = entitlements?.['itps:breach'];
    if (Array.isArray(value)) return value.length > 0;
    return !!value;
  },
);

export const selectAccessToken = createSelector(
  [selectSessionState],
  (sessionState) => sessionState.accessToken,
);

export const selectRefreshToken = createSelector(
  [selectSessionState],
  (sessionState) => sessionState.refreshToken,
);

export const selectToken = createSelector(
  [selectSessionState],
  (sessionState) => sessionState.token,
);

export const selectAgentId = createSelector(
  [selectSessionState],
  (sessionState) => sessionState.agentId,
);

export const selectMissingPii = createSelector(
  [selectSessionState],
  (sessionState) => sessionState.missingPii,
);

export const selectIsUserAgent = createSelector(
  [selectAgentId],
  (agentId) => !!agentId,
);

/**
 * The user's previous visit as reported by the sign-in `profile` — i.e. before
 * the backend stamped this login. `null` on a genuine first visit.
 *
 * Use this, not `selectUserLastVisitedAt`, for any "is this their first visit"
 * decision: the user-slice value comes from `GET api/client/users/{id}`, which
 * returns the post-stamp timestamp and so reads as "now" even for a brand-new
 * user. It also lands after sign-in, so a gate reading it flips mid-boot.
 */
export const selectSessionLastVisitedAt = createSelector(
  [selectSessionState],
  (sessionState) => sessionState.lastVisitedAt,
);

/**
 * Durable tour flags from the sign-in response. Defaulted at the selector as
 * well as in `initialState`, so a slice rehydrated from before this field
 * existed still yields a usable object rather than `undefined`.
 */
export const selectTourAttributes = createSelector(
  [selectSessionState],
  (sessionState) =>
    sessionState.userAttributes ?? { skipTourCount: 0, dontShowTour: false },
);

export const selectTourSkipCount = createSelector(
  [selectTourAttributes],
  (attributes) => attributes.skipTourCount,
);

export const selectTourDontShow = createSelector(
  [selectTourAttributes],
  (attributes) => attributes.dontShowTour,
);

/**
 * Client-side entitlement synthesis. Angular parity —
 * `enrollment.service.ts:898-907` pushes derived feature keys into
 * the user's feature list based on brand + raw JWT entitlements.
 *
 * `public_record_trace` — on the Minor Defense brand only, when the
 * raw `itps:ssn_trace` entitlement includes `'ssnt'`, treat the user
 * as entitled to Public Record Trace. The BE does not send a native
 * `public_record_trace` entitlement (see the Angular comment on
 * line 902: *"Back end not sending any entitlement for public record
 * trace"*), so this synthesis is the single source of truth.
 *
 * Add future brand-derived features here; keep everything server-
 * sourced flowing through `sessionState.features` untouched.
 */
function synthesizeBrandDerivedFeatures(
  baseFeatures: string[],
  brandCode: string,
): string[] {
  // Angular has TWO derivation paths for `public_record_trace` and
  // they disagree:
  //   1. `enrollment.service.ts:898-907` — enrollment-only, strict:
  //      requires `itps:ssn_trace` to include 'ssnt'.
  //   2. `user-plan.service.ts:99-111` — runs on every session load,
  //      just checks `brand === 'dminor'`.
  // The runtime dashboard uses path #2, so the card ships to every
  // dminor user regardless of `itps:ssn_trace`. Mirror that here.
  // If a future brand-derived feature needs the raw entitlements,
  // add them back as a parameter alongside `entitlements` on the
  // caller in `selectUserFeatures`.
  if (brandCode !== BRAND_CODES.minorDefense) return baseFeatures;

  // Skip if the BE ever starts sending it natively — keep the
  // features list idempotent so `.includes()` checks stay honest.
  if (baseFeatures.includes('public_record_trace')) return baseFeatures;

  return [...baseFeatures, 'public_record_trace'];
}

export const selectUserFeatures = createSelector(
  [selectSessionState, selectSessionBrandCode],
  (sessionState, brandCode) =>
    synthesizeBrandDerivedFeatures(sessionState.features, brandCode),
);

export const selectUserOffline = createSelector(
  [selectSessionState],
  (sessionState) => sessionState.userOffline,
);

export const selectUserSessionId = createSelector(
  [selectSessionState],
  (sessionState) => sessionState.sessionId ?? '',
);

export const selectCurrentReport = createSelector(
  [selectSessionState],
  (sessionState) => sessionState.currentReport ?? null,
);

export const selectMigrateReport = createSelector(
  [selectSessionState],
  (sessionState) => sessionState.migrateReport ?? false,
);

// User's active plan grants White Glove Service → show the dashboard card.
// Angular checks presence of the `id_theft_resolution` entitlement KEY,
// ignoring the nested value code (which may vary, e.g. `wgr`). Mirror that
// here by checking the `itps:`-prefixed key, like selectHasAddressMonitoring.
export const selectHasWhiteGloveService = createSelector(
  [selectEntitlements],
  (entitlements) => {
    const value = entitlements?.[WGS_ENTITLEMENT];
    if (Array.isArray(value)) return value.length > 0;
    return !!value;
  },
);

// vault selectors

export const getAccessTokenDecoded = (state: RootState) => {
  if (!state.session.accessToken) return '';
  const accessToken = jose.decodeJwt(state.session.accessToken);
  if (typeof accessToken === 'object' && accessToken) {
    return accessToken as any;
  }
  return '';
};

export const getAccessTokenAlias = (state: RootState) => {
  if (!state.session.accessToken) return '';
  const accessToken = getAccessTokenDecoded(state);
  if (typeof accessToken === 'object' && accessToken) {
    return accessToken.alias;
  }
  return '';
};

export const getAccessTokenExpires = (state: RootState) => {
  if (!state.session.accessToken) return new Date(0);
  const accessToken = getAccessTokenDecoded(state);
  if (typeof accessToken === 'object' && accessToken) {
    if (
      accessToken.exp &&
      isValidDate(new Date(accessToken.exp * 1e3)?.toString())
    ) {
      return new Date(accessToken.exp * 1e3);
    }
    return new Date(0);
  }
  return new Date(0);
};

export const getIsAccessTokenValid = (state: RootState) => {
  const accessTokenExpires = getAccessTokenExpires(state);
  return accessTokenExpires.getTime() > Date.now();
};
